import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { activitiesApi, activityKeys, activityTypesApi, activityTypeKeys } from '../api';
import type { ActivityFilters, StartActivityDto, StopActivityDto, CreateActivityTypeDto, UpdateActivityTypeDto } from '../types';

// Activities hooks
export function useActivities(filters: ActivityFilters) {
  return useQuery({
    queryKey: activityKeys.list(filters),
    queryFn: () => activitiesApi.getAll(filters),
  });
}

export function useActivity(id: string | number | undefined) {
  return useQuery({
    queryKey: activityKeys.detail(id ?? ''),
    queryFn: () => activitiesApi.getById(id!),
    enabled: !!id,
  });
}

export function useActiveActivity() {
  return useQuery({
    queryKey: activityKeys.active(),
    queryFn: () => activitiesApi.getActive(),
    retry: 1,
  });
}

export function useActivityMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const startMutation = useMutation({
    mutationFn: (data: StartActivityDto) => activitiesApi.start(data),
    onSuccess: () => {
      toast.success(t('activityStartedSuccess'));
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorStartingActivity')));
    },
  });

  const stopMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data?: StopActivityDto }) =>
      activitiesApi.stop(id, data),
    onSuccess: () => {
      toast.success(t('activityStoppedSuccess'));
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorStoppingActivity')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => activitiesApi.delete(id),
    onSuccess: () => {
      toast.success(t('activityDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingActivity')));
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: ({ url, data }: { url: string; data?: Record<string, unknown> }) =>
      activitiesApi.changeStatus(url, data),
    onSuccess: () => {
      toast.success(t('activityStatusChangedSuccess'));
      void queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorChangingActivityStatus')));
    },
  });

  return {
    startMutation,
    stopMutation,
    deleteMutation,
    changeStatusMutation,
  };
}

// Activity Types hooks
export function useActivityTypes(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: activityTypeKeys.list(params),
    queryFn: () => activityTypesApi.getAll(params),
  });
}

export function useActivityType(id: string | number | undefined) {
  return useQuery({
    queryKey: activityTypeKeys.detail(id ?? ''),
    queryFn: () => activityTypesApi.getById(id!),
    enabled: !!id,
  });
}

export function useActivityTypeMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateActivityTypeDto) => activityTypesApi.create(data),
    onSuccess: () => {
      toast.success(t('activityTypeCreatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: activityTypeKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingActivityType')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateActivityTypeDto }) =>
      activityTypesApi.update(id, data),
    onSuccess: () => {
      toast.success(t('activityTypeUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: activityTypeKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingActivityType')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => activityTypesApi.delete(id),
    onSuccess: () => {
      toast.success(t('activityTypeDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: activityTypeKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingActivityType')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
